import React, { useCallback, useState } from "react"
import _ from "lodash"
import { Grid, Typography, Select, MenuItem } from "@material-ui/core"

import { AIFollowUpAttempt, AIFollowUpIntention } from "@/protocols/chatBot"

import { SvgIcon } from "@/components"
import { SparklesIcon } from "@/assets/icons"

import useChatBotFlowConstructorStore from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowConstructorStore"

import BlockBodySectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionEditor"
import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/AIFollowUpBlock/AIFollowUpAttemptSectionEditor/styles"

type AIFollowUpFrequencyOption = {
	waitTimeInMinutes: number
	label: string
}

const AIFollowUpFrequencyOptions: AIFollowUpFrequencyOption[] = [
	{ waitTimeInMinutes: 1, label: "1 minuto" },
	{ waitTimeInMinutes: 30, label: "30 minutos" },
	{ waitTimeInMinutes: 60, label: "1 hora" },
	{ waitTimeInMinutes: 120, label: "2 horas" },
	{ waitTimeInMinutes: 360, label: "6 horas" },
	{ waitTimeInMinutes: 720, label: "12 horas" },
	{ waitTimeInMinutes: 1440, label: "24 horas" }
]

const AIFollowUpIntentionLabels: Record<AIFollowUpIntention, string> = {
	"first-contact": "Retomar contato",
	reinforcement: "Reforço",
	closing: "Encerramento"
}

const DEFAULT_ADDITIONAL_ATTEMPT_WAIT_TIME_IN_MINUTES = 30
const DEFAULT_ATTEMPT_INTENTION: AIFollowUpIntention = "first-contact"

const buildBaseAttempt = (attempt?: AIFollowUpAttempt): AIFollowUpAttempt => (
	attempt || {
		waitTimeInMinutes: DEFAULT_ADDITIONAL_ATTEMPT_WAIT_TIME_IN_MINUTES,
		intention: DEFAULT_ATTEMPT_INTENTION
	}
)

type AIFollowUpAttemptSectionEditorProps = {
	attempt?: AIFollowUpAttempt
	isWabaChannel: boolean
	onSave: (attempt: AIFollowUpAttempt) => void
	onDelete?: () => void
	hideChildSelection?: boolean
	children: React.ReactElement
}

const AIFollowUpAttemptSectionEditor: React.FC<AIFollowUpAttemptSectionEditorProps> = (props) => {
	const {
		attempt,
		isWabaChannel,
		onSave,
		onDelete,
		hideChildSelection,
		children
	} = props

	const classes = useStyles()
	const chatBotFlowConstructorStore = useChatBotFlowConstructorStore()

	const availableWabaChannelMessageTemplates = chatBotFlowConstructorStore.constructionResources.wabaChannelMessageTemplates
		.filter(template => template.message?.type === "text" && !template.message.extraData?.button)

	const handleLoadTemplates = useCallback(async () => {
		await chatBotFlowConstructorStore.loadSpecificConstructionResources("wabaChannelMessageTemplates")
	}, [])

	const [draftAttempt, setDraftAttempt] = useState<AIFollowUpAttempt>(() => _.cloneDeep(buildBaseAttempt(attempt)))

	const handleResetDraft = () => {
		setDraftAttempt(_.cloneDeep(buildBaseAttempt(attempt)))
	}

	const handleOpenDrawer = () => {
		handleResetDraft()

		if (isWabaChannel) {
			handleLoadTemplates()
		}
	}

	const handleChangeDraft = (updatedFields: Partial<AIFollowUpAttempt>) => {
		setDraftAttempt(lastDraft => ({ ...lastDraft, ...updatedFields }))
	}

	const handleChangeFallbackTemplate = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
		const selectedTemplateId = Number(event.target.value)
		const selectedTemplate = availableWabaChannelMessageTemplates.find(template => template.id === selectedTemplateId)

		handleChangeDraft({
			wabaTemplate: selectedTemplate ? { id: selectedTemplate.id, name: selectedTemplate.name } : undefined
		})
	}

	return (
		<BlockBodySectionEditor
			title="CONFIGURAR TENTATIVA"
			onOpen={handleOpenDrawer}
			onSave={() => onSave(draftAttempt)}
			onDelete={onDelete}
			hideChildSelection={hideChildSelection}
			AsideDrawerProps={{
				width: "420px",
				children: (
					<Grid>
						<Grid className={classes.editorContainer}>
							<Grid className={classes.editableSentence}>
								<span>Enviar uma mensagem de follow-up com a intenção de </span>
								<Select
									value={draftAttempt.intention}
									onChange={(event) => handleChangeDraft({ intention: event.target.value as AIFollowUpIntention })}
									className={classes.inlineSelect}
									disableUnderline
								>
									{Object.entries(AIFollowUpIntentionLabels).map(([value, label]) => (
										<MenuItem
											key={value}
											value={value}
										>
											{label}
										</MenuItem>
									))}
								</Select>
								<span>.</span>
								{isWabaChannel && (
									<>
										<span> Caso o contato não possua janela de comunicação ativa, enviar o template </span>
										<Select
											value={draftAttempt.wabaTemplate?.id || ""}
											onChange={handleChangeFallbackTemplate}
											className={classes.inlineSelect}
											disableUnderline
										>
											{availableWabaChannelMessageTemplates.map(template => (
												<MenuItem
													key={template.id}
													value={template.id}
													disabled={template.status !== "approved"}
												>
													{template.name}
												</MenuItem>
											))}
										</Select>
										<span>.</span>
									</>
								)}
								<span> Após o envio da mensagem, aguardar </span>
								<Select
									value={draftAttempt.waitTimeInMinutes}
									onChange={(event) => handleChangeDraft({ waitTimeInMinutes: Number(event.target.value) })}
									className={classes.inlineSelect}
									disableUnderline
								>
									{AIFollowUpFrequencyOptions.map(option => (
										<MenuItem
											key={option.waitTimeInMinutes}
											value={option.waitTimeInMinutes}
										>
											{option.label}
										</MenuItem>
									))}
								</Select>
								<span> para o contato responder.</span>
							</Grid>
						</Grid>

						<Grid className={classes.callout}>
							<Grid className={classes.calloutIconContainer}>
								<SvgIcon
									icon={SparklesIcon}
									fontSize="small"
								/>
							</Grid>
							<Typography className={classes.calloutText}>
								A IA lê as mensagens do último protocolo de atendimento e monta automaticamente a mensagem de follow-up. Se o cliente não responder, o fluxo segue por uma saída própria do bloco, que você pode direcionar para o passo que quiser.
							</Typography>
						</Grid>
					</Grid>
				)
			}}
			DeletePopConfirmProps={{
				title: "Excluir tentativa",
				description: "Tem certeza? Essa ação é irreversível.",
				confirmButtonText: "EXCLUIR"
			}}
		>
			{children}
		</BlockBodySectionEditor>
	)
}

export default AIFollowUpAttemptSectionEditor
